import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const C = { navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981', error: '#EF4444',
            grey: '#6B7280', border: '#E5E7EB', amber: '#F59E0B' };

const STATUS = {
  not_started: { label: 'Waiting to start', color: C.grey,    bg: '#F3F4F6' },
  released:    { label: 'Released',          color: '#1D4ED8', bg: '#DBEAFE' },
  in_progress: { label: 'In progress',       color: C.amber,   bg: '#FEF3C7' },
  paused:      { label: 'Paused',            color: C.error,   bg: '#FEE2E2' },
};

const mmss = (s) => { s = Math.max(0, Math.floor(s || 0)); const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, '0')}`; };

export default function MockLiveMonitor({ showInvigilator = true }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const tick = useCallback(async () => {
    try { const { data } = await api.get('/api/admin/mock-tests/live'); setRows(Array.isArray(data) ? data : []); } catch {}
    setLoaded(true);
  }, []);

  useEffect(() => {
    let alive = true;
    const run = () => { if (alive) tick(); };
    run();
    const id = setInterval(run, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [tick]);

  const act = async (url, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await api.post(url); toast.success('Done'); tick(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Action failed'); }
  };

  const ABtn = ({ label, color, onClick }) => (
    <button onClick={onClick} style={{ padding: '5px 12px', borderRadius: 6, background: color, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, marginLeft: 4 }}>{label}</button>
  );

  if (!loaded) return <div style={{ color: C.grey, padding: 24 }}>Loading…</div>;
  if (rows.length === 0) return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 40, textAlign: 'center', color: C.grey }}>
      No active mock tests right now. Activated tests appear here grouped by room. Refreshes every 5s.
    </div>
  );

  // Group by slot/room
  const groups = {};
  rows.forEach(r => {
    const key = r.slot_id || ('noslot-' + r.room);
    if (!groups[key]) groups[key] = { slot_id: r.slot_id, room: r.room, slot_time: r.slot_time, test: r.test_type, invigilator: r.invigilator_name, items: [] };
    groups[key].items.push(r);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {Object.values(groups).map((g, gi) => {
        const waiting = g.items.filter(i => i.status === 'not_started').length;
        return (
          <div key={gi} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontWeight: 700, color: C.navy }}>
                🏫 {g.room}{g.slot_time ? ` · ${g.slot_time}` : ''} <span style={{ color: C.grey, fontWeight: 500, fontSize: 13 }}>· {g.test} · {g.items.length} candidate(s){showInvigilator && g.invigilator ? ` · Invigilator: ${g.invigilator}` : ''}</span>
              </div>
              {g.slot_id && waiting > 0 && (
                <ABtn label={`▶ Start Room (${waiting})`} color={C.success}
                  onClick={() => act(`/api/admin/mock-tests/slots/${g.slot_id}/release-all`, `Start the test for all ${waiting} waiting candidate(s) in ${g.room}?`)} />
              )}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: C.grey, fontSize: 12 }}>
                  <th style={{ padding: '8px 16px' }}>Candidate</th>
                  <th style={{ padding: '8px 16px' }}>Status</th>
                  <th style={{ padding: '8px 16px' }}>Progress</th>
                  <th style={{ padding: '8px 16px' }}>Time left</th>
                  <th style={{ padding: '8px 16px' }}>Integrity</th>
                  <th style={{ padding: '8px 16px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map(r => {
                  const st = STATUS[r.status] || { label: r.status, color: C.grey, bg: '#F3F4F6' };
                  return (
                    <tr key={r.session_id} style={{ borderTop: `1px solid ${C.border}`, background: r.flagged ? '#FEF2F2' : '#fff' }}>
                      <td style={{ padding: '9px 16px', fontWeight: 600, color: C.navy }}>{r.student_name}</td>
                      <td style={{ padding: '9px 16px' }}><span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: st.bg, color: st.color }}>{st.label}</span></td>
                      <td style={{ padding: '9px 16px' }}>{r.answered}/{r.total}</td>
                      <td style={{ padding: '9px 16px', color: r.remaining_seconds < 300 ? C.error : C.navy }}>{r.status === 'in_progress' ? mmss(r.remaining_seconds) : '—'}</td>
                      <td style={{ padding: '9px 16px' }}>{r.flagged ? <span style={{ color: C.error, fontWeight: 700 }}>⚠ {r.tab_switches}</span> : <span style={{ color: C.success }}>OK{r.tab_switches ? ` (${r.tab_switches})` : ''}</span>}</td>
                      <td style={{ padding: '9px 16px', whiteSpace: 'nowrap' }}>
                        {r.status === 'not_started' && <ABtn label="▶ Start" color={C.success} onClick={() => act(`/api/admin/mock-tests/sessions/${r.session_id}/release`)} />}
                        {r.status === 'released' && <span style={{ fontSize: 12, color: C.grey }}>Awaiting candidate</span>}
                        {r.status === 'in_progress' && <ABtn label="⏸ Pause" color={C.amber} onClick={() => act(`/api/admin/mock-tests/sessions/${r.session_id}/pause`)} />}
                        {r.status === 'paused' && <ABtn label="▶ Resume" color={C.success} onClick={() => act(`/api/admin/mock-tests/sessions/${r.session_id}/resume`)} />}
                        {r.status !== 'not_started' && <ABtn label="✕ Cancel" color={C.error} onClick={() => act(`/api/admin/mock-tests/sessions/${r.session_id}/cancel`, 'Cancel this test? The candidate cannot continue.')} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
